import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import {
  Calendar, ChevronLeft, ChevronRight, Users, Plus, Edit2, Trash2, X, Check,
  AlertCircle, Clock, Sun, Moon, Star, Zap, Award, DollarSign, TrendingUp,
  AlertTriangle, Trophy, Gift, Heart, Coffee, Sparkles, Settings, CalendarPlus, Home, Printer
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, getDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

import PageContainer from '../layout/PageContainer';

interface WageRule {
  id: number;
  name: string;
  type: string;
  amount: number;
  conditions: any;
  icon?: string;
  icon_color?: string;
  is_active: boolean;
}

interface Staff {
  id: number;
  display_name: string;
  role_level: number;
  is_owner?: number;
}

interface Shift {
  id: number;
  staff_id: number;
  staff_name: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  shift_type: 'morning' | 'lunch' | 'dinner' | 'full';
}

interface ShiftType {
  id: string | number;
  label: string;
  start_time: string;
  end_time: string;
  start_time2?: string;
  end_time2?: string;
  color: string;
  description?: string;
  break_required?: boolean;
}

interface ShiftCalendarEnhancedProps {
  embedded?: boolean;
}

const ShiftCalendarEnhanced: React.FC<ShiftCalendarEnhancedProps> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState<Partial<Shift> | null>(null);
  const [loading, setLoading] = useState(false);
  const [wageRules, setWageRules] = useState<WageRule[]>([]);
  const [selectedShiftForRules, setSelectedShiftForRules] = useState<Shift | null>(null);
  const [appliedManualRules, setAppliedManualRules] = useState<{ [key: string]: number[] }>({});
  const [showMonthlySettings, setShowMonthlySettings] = useState(false);
  
  // 月初設定
  const [monthlySettings, setMonthlySettings] = useState({
    closedDays: [4], // デフォルト: 木曜日（0:日曜〜6:土曜）
    defaultShifts: [
      { staffName: 'カツ', startTime: '11:00', endTime: '21:00', isOwner: true }
    ],
    holidays: [] as Date[] // 不定休の日付
  });
  
  // 連続登録モード
  const [quickMode, setQuickMode] = useState(true);
  const [selectedStaffIds, setSelectedStaffIds] = useState<number[]>([]);
  const [selectedShiftType, setSelectedShiftType] = useState<string | number>('full');
  const [deleteMode, setDeleteMode] = useState(false);

  // シフトタイプの定義（初期値は従来のハードコードされた値）
  const [shiftTypes, setShiftTypes] = useState<ShiftType[]>([
    { id: 'full', label: '通し', start_time: '10:00', end_time: '19:00', color: 'bg-green-100 text-green-800' },
    { id: 'lunch', label: 'ランチ', start_time: '10:00', end_time: '15:00', color: 'bg-blue-100 text-blue-800' },
    { id: 'dinner', label: 'ディナー', start_time: '16:00', end_time: '21:00', color: 'bg-purple-100 text-purple-800' },
    { id: 'morning', label: '朝', start_time: '07:00', end_time: '11:00', color: 'bg-yellow-100 text-yellow-800' },
  ]);

  // フォーム
  const [formData, setFormData] = useState({
    staff_id: '',
    shift_date: '',
    start_time: '10:00',
    end_time: '19:00',
    shift_type: 'full' as const
  });

  useEffect(() => {
    fetchStaff();
    fetchShifts();
    fetchWorkPatterns();
    fetchWageRules();
    fetchMonthlySettings();
  }, [currentMonth]);

  const fetchWageRules = async () => {
    try {
      const response = await apiClient.get('/wage-rules');
      if (response.data) {
        setWageRules(response.data);
      }
    } catch (error) {
      console.error('時給ルール取得エラー:', error);
    }
  };

  const fetchMonthlySettings = async () => {
    try {
      const response = await apiClient.get('/shifts-enhanced/monthly-settings');
      if (response.data.success) {
        const { closedDays, holidays, defaultShifts } = response.data.data;
        setMonthlySettings({
          closedDays: closedDays || [4],
          holidays: holidays.map((h: string) => new Date(h)) || [],
          defaultShifts: defaultShifts || [
            { staffName: 'カツ', startTime: '11:00', endTime: '21:00', isOwner: true }
          ]
        });
      }
    } catch (error) {
      console.error('月初設定取得エラー:', error);
    }
  };

  const fetchWorkPatterns = async () => {
    try {
      const response = await apiClient.get('/work-patterns');
      if (response.data && response.data.length > 0) {
        // データベースから取得した勤務パターンをシフトタイプ形式に変換
        const patterns = response.data.map((pattern: any) => ({
          id: pattern.id,
          label: pattern.name,
          start_time: pattern.start_time1,
          end_time: pattern.end_time1,
          start_time2: pattern.start_time2,
          end_time2: pattern.end_time2,
          color: pattern.background_color || 'bg-gray-100 text-gray-800',
          description: pattern.description,
          break_required: pattern.break_required
        }));
        setShiftTypes(patterns);
        // 最初のパターンを選択
        if (patterns.length > 0 && !selectedShiftType) {
          setSelectedShiftType(patterns[0].id);
        }
      }
    } catch (error) {
      console.error('勤務パターン取得エラー:', error);
      // エラー時はデフォルト値を使用
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await apiClient.get('/staff/list');
      const staffList = response.data.data || [];
      // role_levelが数値なので、adminではないスタッフをフィルタリング
      // role_level: 2=開発者, 1=オーナー, 0=スタッフ
      // 全員を表示（adminという概念はない）
      console.log('取得したスタッフリスト:', staffList);
      setStaff(staffList);
    } catch (error) {
      console.error('スタッフ取得エラー:', error);
    }
  };

  const fetchShifts = async () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    
    try {
      const response = await apiClient.get('/shifts-enhanced/published', {
        params: {
          start_date: format(start, 'yyyy-MM-dd'),
          end_date: format(end, 'yyyy-MM-dd')
        }
      });
      setShifts(response.data);
    } catch (error) {
      console.error('シフト取得エラー:', error);
    }
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    
    // 月初日の曜日を取得（0:日曜日〜6:土曜日）
    const firstDayOfWeek = getDay(start);
    
    // 月初日より前の空白セルを追加
    const paddingDays = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      // 前月の日付を計算して追加（表示はしないが、グリッドを埋めるため）
      const paddingDate = new Date(start);
      paddingDate.setDate(paddingDate.getDate() - (firstDayOfWeek - i));
      paddingDays.push(paddingDate);
    }
    
    // すべてのDateオブジェクトが有効か確認
    const allDays = [...paddingDays, ...days];
    return allDays.filter(d => d instanceof Date && !isNaN(d.getTime()));
  };

  const getShiftsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts.filter(shift => shift.shift_date === dateStr);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  // クイックモードでの日付クリック
  const handleQuickDateClick = async (date: Date) => {
    // 定休日・不定休チェック
    const dayOfWeek = getDay(date);
    const isClosedDay = monthlySettings.closedDays.includes(dayOfWeek);
    const isHoliday = monthlySettings.holidays.some(
      h => format(h, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
    
    if ((isClosedDay || isHoliday) && !deleteMode) {
      alert('定休日・不定休にはシフトを登録できません');
      return;
    }
    
    if (deleteMode) {
      // 削除モード
      const dateShifts = getShiftsForDate(date);
      const shiftsToDelete = dateShifts.filter(shift => 
        selectedStaffIds.includes(shift.staff_id)
      );

      if (shiftsToDelete.length === 0) {
        return;
      }

      if (!confirm(`${format(date, 'M/d')}の選択したスタッフのシフトを削除しますか？`)) {
        return;
      }

      setLoading(true);
      try {
        for (const shift of shiftsToDelete) {
          await apiClient.delete(`/shifts-enhanced/${shift.id}`);
        }
        fetchShifts();
      } catch (error) {
        alert('シフト削除に失敗しました');
      } finally {
        setLoading(false);
      }
    } else {
      // 登録モード
      if (selectedStaffIds.length === 0) {
        alert('スタッフを選択してください');
        return;
      }

      const selectedType = shiftTypes.find(t => t.id === selectedShiftType);
      if (!selectedType) return;

      setLoading(true);
      try {
        const shiftsToAdd = selectedStaffIds.map(staffId => ({
          staff_id: staffId,
          shift_date: format(date, 'yyyy-MM-dd'),
          start_time: selectedType.start_time,
          end_time: selectedType.end_time,
          shift_type: selectedShiftType
        }));

        console.log('送信するシフトデータ:', shiftsToAdd);
        console.log('選択されたスタッフID:', selectedStaffIds);

        await apiClient.post('/shifts-enhanced/publish', {
          shifts: shiftsToAdd,
          published_by: 1 // TODO: 実際のログインユーザーIDを取得
        });

        fetchShifts();
      } catch (error: any) {
        alert(error.response?.data?.error || 'シフト追加に失敗しました');
      } finally {
        setLoading(false);
      }
    }
  };

  // 通常モードでの日付クリック
  const handleNormalDateClick = (date: Date) => {
    // 定休日・不定休チェック
    const dayOfWeek = getDay(date);
    const isClosedDay = monthlySettings.closedDays.includes(dayOfWeek);
    const isHoliday = monthlySettings.holidays.some(
      h => format(h, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
    
    if (isClosedDay || isHoliday) {
      alert('定休日・不定休にはシフトを登録できません');
      return;
    }
    
    setSelectedDate(date);
    setFormData({
      ...formData,
      shift_date: format(date, 'yyyy-MM-dd')
    });
    setShowModal(true);
  };

  const handleDateClick = (date: Date) => {
    if (quickMode) {
      handleQuickDateClick(date);
    } else {
      handleNormalDateClick(date);
    }
  };

  const handleAddShift = async () => {
    if (!formData.staff_id || !formData.shift_date) {
      alert('スタッフと日付を選択してください');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/shifts-enhanced/publish', {
        shifts: [{
          ...formData,
          staff_id: parseInt(formData.staff_id)
        }],
        published_by: 1 // TODO: 実際のログインユーザーIDを取得
      });
      alert('シフトを追加しました');
      fetchShifts();
      setShowModal(false);
      resetForm();
    } catch (error: any) {
      alert(error.response?.data?.error || 'シフト追加に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShift = async (shiftId: number) => {
    if (!confirm('このシフトを削除しますか？')) return;

    try {
      await apiClient.delete(`/shifts-enhanced/${shiftId}`);
      fetchShifts();
    } catch (error) {
      alert('シフト削除に失敗しました');
    }
  };

  const resetForm = () => {
    setFormData({
      staff_id: '',
      shift_date: '',
      start_time: '10:00',
      end_time: '19:00',
      shift_type: 'full'
    });
    setEditingShift(null);
  };

  const toggleStaffSelection = (staffId: number) => {
    setSelectedStaffIds(prev => {
      if (prev.includes(staffId)) {
        return prev.filter(id => id !== staffId);
      }
      return [...prev, staffId];
    });
  };

  // 月初一括登録処理
  const handleMonthlyRegistration = async () => {
    if (!confirm('今月のシフトを一括登録しますか？\n※既存のシフトは上書きされません')) {
      return;
    }

    setLoading(true);
    try {
      const days = getDaysInMonth();
      const shiftsToAdd = [];

      for (const date of days) {
        // 当月のみ処理
        if (!isSameMonth(date, currentMonth)) continue;

        const dayOfWeek = getDay(date);
        
        // 定休日チェック
        const isClosedDay = monthlySettings.closedDays.includes(dayOfWeek);
        
        // 不定休チェック
        const isHoliday = monthlySettings.holidays.some(
          h => format(h, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
        );

        if (isClosedDay || isHoliday) {
          // 定休日の場合はスキップ（または「定休日」として登録）
          continue;
        }

        // デフォルトシフトを登録
        for (const defaultShift of monthlySettings.defaultShifts) {
          const staffMember = staff.find(s => s.display_name === defaultShift.staffName);
          if (staffMember) {
            // 既存シフトがないか確認
            const existingShift = shifts.find(
              s => s.staff_id === staffMember.id && 
                   s.shift_date === format(date, 'yyyy-MM-dd')
            );
            
            if (!existingShift) {
              // フルタイムシフトタイプを探す（11:00-21:00にマッチするもの）
              const matchingShiftType = shiftTypes.find(
                type => type.start_time === defaultShift.startTime && 
                        type.end_time === defaultShift.endTime
              );
              
              shiftsToAdd.push({
                staff_id: staffMember.id,
                shift_date: format(date, 'yyyy-MM-dd'),
                start_time: defaultShift.startTime,
                end_time: defaultShift.endTime,
                shift_type: matchingShiftType ? matchingShiftType.id : (shiftTypes[0]?.id || 'full')
              });
            }
          }
        }
      }

      // 定休日・不定休に既存シフトがある場合、削除確認
      const holidayDates = [];
      for (const date of days) {
        if (!isSameMonth(date, currentMonth)) continue;
        const dayOfWeek = getDay(date);
        const isClosedDay = monthlySettings.closedDays.includes(dayOfWeek);
        const isHoliday = monthlySettings.holidays.some(
          h => format(h, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
        );
        if (isClosedDay || isHoliday) {
          holidayDates.push(format(date, 'yyyy-MM-dd'));
        }
      }

      // 定休日・不定休に既存シフトがあるか確認
      const shiftsOnHolidays = shifts.filter(s => holidayDates.includes(s.shift_date));
      
      if (shiftsOnHolidays.length > 0) {
        const staffNames = [...new Set(shiftsOnHolidays.map(s => {
          const staffMember = staff.find(st => st.id === s.staff_id);
          return staffMember?.display_name || '不明';
        }))];
        
        if (!confirm(`以下のスタッフの定休日・不定休のシフトを削除しますか？\n${staffNames.join('、')}\n\n削除する日付: ${shiftsOnHolidays.length}件`)) {
          setLoading(false);
          return;
        }
        
        // 定休日のシフトを削除
        for (const shift of shiftsOnHolidays) {
          await apiClient.delete(`/shifts-enhanced/${shift.id}`);
        }
      }

      if (shiftsToAdd.length > 0) {
        await apiClient.post('/shifts-enhanced/publish', {
          shifts: shiftsToAdd,
          published_by: 1
        });
        
        alert(`${shiftsToAdd.length}件のシフトを登録しました`);
        fetchShifts();
      } else {
        fetchShifts();
        alert('登録するシフトがありません');
      }
    } catch (error) {
      console.error('月初登録エラー:', error);
      alert('月初登録に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getShiftTypeColor = (type: string | number) => {
    const shiftType = shiftTypes.find(t => t.id === type || t.id === String(type) || t.id === Number(type));
    const colorValue = shiftType?.color || 'bg-gray-100 text-gray-800';
    
    // 色のマッピングを明示的に定義（Tailwindのパージ問題を回避）
    const colorMap: { [key: string]: string } = {
      'bg-green-100 text-green-800': 'bg-green-100 text-green-800',
      'bg-blue-100 text-blue-800': 'bg-blue-100 text-blue-800',
      'bg-purple-100 text-purple-800': 'bg-purple-100 text-purple-800',
      'bg-yellow-100 text-yellow-800': 'bg-yellow-100 text-yellow-800',
      'bg-orange-100 text-orange-800': 'bg-orange-100 text-orange-800',
      'bg-indigo-100 text-indigo-800': 'bg-indigo-100 text-indigo-800',
      'bg-pink-100 text-pink-800': 'bg-pink-100 text-pink-800',
      'bg-gray-100 text-gray-800': 'bg-gray-100 text-gray-800',
      'bg-red-100 text-red-800': 'bg-red-100 text-red-800',
      'bg-teal-100 text-teal-800': 'bg-teal-100 text-teal-800'
    };
    
    return colorMap[colorValue] || 'bg-gray-100 text-gray-800';
  };

  const getShiftTypeLabel = (type: string | number) => {
    const shiftType = shiftTypes.find(t => t.id === type || t.id === String(type) || t.id === Number(type));
    return shiftType?.label || String(type);
  };

  // シフト表印刷
  const printShiftTable = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });
    const weekDayNames = ['日', '月', '火', '水', '木', '金', '土'];

    const html = `
      <!DOCTYPE html>
      <html lang="ja">
      <head>
        <meta charset="UTF-8">
        <title>シフト表 - ${format(currentMonth, 'yyyy年M月')}</title>
        <style>
          body { font-family: 'MS Gothic', monospace; margin: 20px; }
          h1 { text-align: center; font-size: 18px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #000; padding: 4px 6px; text-align: center; font-size: 11px; }
          th { background-color: #f0f0f0; font-weight: bold; }
          .closed { background-color: #fee; color: #999; }
          .sun { color: red; }
          .sat { color: blue; }
          @media print {
            body { margin: 5mm; }
            @page { size: landscape; }
          }
        </style>
      </head>
      <body>
        <h1>シフト表 - ${format(currentMonth, 'yyyy年M月')}</h1>
        <table>
          <thead>
            <tr>
              <th>スタッフ</th>
              ${days.map(d => {
                const dow = getDay(d);
                const cls = dow === 0 ? 'sun' : dow === 6 ? 'sat' : '';
                const isClosedDay = monthlySettings.closedDays.includes(dow);
                return `<th class="${cls} ${isClosedDay ? 'closed' : ''}">${format(d, 'd')}<br/>${weekDayNames[dow]}</th>`;
              }).join('')}
            </tr>
          </thead>
          <tbody>
            ${staff.map(s => `
              <tr>
                <td style="white-space:nowrap; font-weight:bold;">${s.display_name}</td>
                ${days.map(d => {
                  const dateStr = format(d, 'yyyy-MM-dd');
                  const dow = getDay(d);
                  const isClosedDay = monthlySettings.closedDays.includes(dow);
                  const isHoliday = monthlySettings.holidays.some(
                    (h: Date) => format(h, 'yyyy-MM-dd') === dateStr
                  );
                  const dayShifts = shifts.filter(sh => sh.staff_id === s.id && sh.shift_date === dateStr);

                  if (isClosedDay || isHoliday) {
                    return `<td class="closed">${dayShifts.length > 0 ? dayShifts.map(sh => sh.start_time.slice(0,5) + '-' + sh.end_time.slice(0,5)).join('<br/>') : '-'}</td>`;
                  }

                  if (dayShifts.length === 0) return '<td>-</td>';
                  return `<td>${dayShifts.map(sh => sh.start_time.slice(0,5) + '-' + sh.end_time.slice(0,5)).join('<br/>')}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 100);
  };

  // アイコンコンポーネントを取得
  const getIconComponent = (iconName?: string) => {
    const iconMap: { [key: string]: React.FC<any> } = {
      sun: Sun,
      moon: Moon,
      star: Star,
      zap: Zap,
      'alert-triangle': AlertTriangle,
      award: Award,
      'dollar-sign': DollarSign,
      'trending-up': TrendingUp,
      trophy: Trophy,
      gift: Gift,
      heart: Heart,
      coffee: Coffee,
      sparkles: Sparkles
    };
    
    return iconMap[iconName || ''] || null;
  };

  // 適用される時給ルールを取得
  const getApplicableWageRules = (date: Date, shiftType?: string | number, staffName?: string, shiftId?: number) => {
    // カツ氏（オーナー）には時給ルールを適用しない
    if (staffName && (staffName === 'カツ' || staffName.includes('オーナー'))) {
      return [];
    }
    
    // dateが正しく渡されているか確認
    if (!date || !(date instanceof Date)) {
      console.warn('Invalid date passed to getApplicableWageRules:', date);
      return [];
    }
    
    const dayOfWeek = getDay(date);
    const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
    const dayName = dayNames[dayOfWeek];
    
    // 自動適用ルール
    const autoRules = wageRules.filter(rule => {
      if (!rule.is_active) return false;
      
      // 曜日ベースのルール
      if (rule.type === 'weekend' && rule.conditions?.days) {
        return rule.conditions.days.includes(dayName);
      }
      
      // 時間ベースのルール（シフトタイプから判断）
      if (rule.type === 'time' && shiftType) {
        const shift = shiftTypes.find(t => t.id === shiftType);
        if (shift && rule.conditions?.start_time && rule.conditions?.end_time) {
          // 夜間手当の判定（簡易版）
          const ruleStartHour = parseInt(rule.conditions.start_time.split(':')[0]);
          const shiftEndHour = parseInt(shift.end_time.split(':')[0]);
          return shiftEndHour >= ruleStartHour;
        }
      }
      
      // 繁忙期手当は手動適用のため、ここでは自動適用しない
      if (rule.type === 'peak') {
        return false;
      }
      
      return false;
    });
    
    // 手動適用ルールを追加
    if (shiftId) {
      const manualRuleIds = appliedManualRules[`shift-${shiftId}`] || [];
      const manualRules = wageRules.filter(rule => manualRuleIds.includes(rule.id));
      return [...autoRules, ...manualRules];
    }
    
    return autoRules;
  };

  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  return (
    <div className={embedded ? "" : "p-6 bg-gray-50 min-h-screen"}>
      <div className="flex gap-6">
        {/* メインカレンダー部分 */}
        <div className={`flex-1 ${embedded ? "" : "max-w-5xl"}`}>
          {/* ヘッダー */}
          <div className={embedded ? "bg-white rounded-lg shadow p-4 mb-4" : "bg-white rounded-lg shadow-lg p-6 mb-6"}>
            <div className="flex items-center justify-between mb-4">
              {!embedded && (
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Calendar className="h-6 w-6 text-blue-600" />
                  シフトカレンダー
                </h1>
              )}
              <div className={`flex items-center gap-4 ${embedded ? 'mx-auto' : ''}`}>
                {!embedded && (
                  <button
                    onClick={() => navigate('/home')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 mr-4"
                  >
                    <Home className="w-5 h-5" />
                    ホーム
                  </button>
                )}
                <button
                  onClick={handlePrevMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <h2 className="text-xl font-bold">
                  {format(currentMonth, 'yyyy年 M月', { locale: ja })}
                </h2>
                <button
                  onClick={handleNextMonth}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={printShiftTable}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                  title="シフト表を印刷"
                >
                  <Printer className="h-4 w-4" />
                  印刷
                </button>
                <button
                  onClick={handleMonthlyRegistration}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  title="今月のシフトを一括登録"
                >
                  <CalendarPlus className="h-4 w-4" />
                  月初登録
                </button>
                <button
                  onClick={() => setShowMonthlySettings(true)}
                  className="p-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  title="月初登録の設定"
                >
                  <Settings className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setQuickMode(!quickMode)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    quickMode 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {quickMode ? '連続登録モード' : '個別登録モード'}
                </button>
              </div>
            </div>
          </div>

          {/* カレンダー */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="grid grid-cols-7 gap-px bg-gray-200">
              {/* 曜日ヘッダー */}
              {weekDays.map((day, index) => (
                <div
                  key={day}
                  className={`bg-gray-50 p-3 text-center font-semibold text-sm ${
                    index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-gray-700'
                  }`}
                >
                  {day}
                </div>
              ))}

              {/* 日付セル */}
              {getDaysInMonth().map((date) => {
                const dayShifts = getShiftsForDate(date);
                const dayOfWeek = getDay(date);
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const isClosedDay = monthlySettings.closedDays.includes(dayOfWeek);
                const isIrregularHoliday = monthlySettings.holidays.some(
                  h => format(h, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
                );
                const isHoliday = isClosedDay || isIrregularHoliday;

                return (
                  <div
                    key={date.toISOString()}
                    className={`min-h-[100px] p-2 relative ${
                      !isSameMonth(date, currentMonth) ? 'opacity-30' : ''
                    } ${
                      isToday(date)
                        ? 'bg-yellow-100 ring-2 ring-yellow-500 ring-inset'
                        : isClosedDay
                        ? 'bg-red-50'
                        : isIrregularHoliday
                        ? 'bg-orange-50'
                        : isWeekend
                        ? 'bg-gray-50'
                        : 'bg-white'
                    } ${quickMode ? 'hover:bg-yellow-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} transition-all`}
                    onClick={() => handleDateClick(date)}
                  >
                    <div className={`text-sm font-semibold mb-1 flex items-center ${
                      isClosedDay || isIrregularHoliday ? 'text-red-600' :
                      dayOfWeek === 0 ? 'text-red-600' :
                      dayOfWeek === 6 ? 'text-blue-600' :
                      'text-gray-700'
                    }`}>
                      {format(date, 'd')}
                      {isToday(date) && (
                        <span className="ml-1 px-1 py-0.5 bg-yellow-500 text-white text-xs rounded font-bold">
                          今日
                        </span>
                      )}
                      {isClosedDay && <span className="ml-1 text-xs">定休</span>}
                      {isIrregularHoliday && !isClosedDay && <span className="ml-1 text-xs">休</span>}
                    </div>
                    
                    {/* シフト表示 */}
                    <div className="space-y-1">
                      {dayShifts.slice(0, 3).map((shift) => {
                        const applicableRules = getApplicableWageRules(date, shift.shift_type, shift.staff_name, shift.id);
                        return (
                          <div
                            key={shift.id}
                            className={`text-xs px-1 py-0.5 rounded cursor-pointer ${getShiftTypeColor(shift.shift_type)}`}
                            onClick={(e) => {
                              if (!quickMode) {
                                e.stopPropagation();
                              }
                            }}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // カツ氏以外の場合のみ時給ルール適用画面を開く
                              if (shift.staff_name !== 'カツ' && !shift.staff_name.includes('オーナー')) {
                                setSelectedShiftForRules(shift);
                              }
                            }}
                            title="右クリックで時給ルールを手動適用"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1 min-w-0">
                                <span className="truncate font-medium">
                                  {shift.staff_name}
                                </span>
                                {/* 時給ルールアイコン表示 */}
                                {applicableRules.length > 0 && (
                                  <div className="flex items-center gap-0.5">
                                    {applicableRules.slice(0, 2).map(rule => {
                                      const IconComponent = getIconComponent(rule.icon);
                                      return IconComponent ? (
                                        <IconComponent 
                                          key={rule.id}
                                          className={`h-3 w-3 ${rule.icon_color || 'text-gray-600'}`}
                                          title={`${rule.name}: +${rule.amount}円`}
                                        />
                                      ) : null;
                                    })}
                                  </div>
                                )}
                              </div>
                              {!quickMode && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteShift(shift.id);
                                  }}
                                  className="hover:text-red-600 ml-1 flex-shrink-0"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {dayShifts.length > 3 && (
                        <div className="text-xs text-gray-500 text-center">
                          +{dayShifts.length - 3}件
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* 右側の連続登録パネル */}
        {quickMode && (
          <div className="w-80 bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              連続登録設定
            </h3>

            {/* モード切替 */}
            <div className="mb-6">
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteMode(false)}
                  className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                    !deleteMode 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Plus className="h-4 w-4 inline mr-1" />
                  登録
                </button>
                <button
                  onClick={() => setDeleteMode(true)}
                  className={`flex-1 px-3 py-2 rounded-lg transition-colors ${
                    deleteMode 
                      ? 'bg-red-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Trash2 className="h-4 w-4 inline mr-1" />
                  削除
                </button>
              </div>
            </div>

            {/* スタッフ選択 */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold mb-2">
                ① スタッフを選択
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {staff.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedStaffIds.includes(s.id)}
                      onChange={() => toggleStaffSelection(s.id)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="flex-1">{s.display_name}</span>
                    {selectedStaffIds.includes(s.id) && (
                      <Check className="h-4 w-4 text-green-500" />
                    )}
                  </label>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => setSelectedStaffIds(staff.map(s => s.id))}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  全選択
                </button>
                <button
                  onClick={() => setSelectedStaffIds([])}
                  className="text-xs text-gray-600 hover:text-gray-700"
                >
                  クリア
                </button>
              </div>
            </div>

            {/* シフトタイプ選択（登録モードのみ） */}
            {!deleteMode && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-2">
                  ② 勤務形態を選択
                </h4>
                <div className="space-y-2">
                  {shiftTypes.map((type) => (
                    <label
                      key={type.id}
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="shiftType"
                        value={type.id}
                        checked={selectedShiftType === type.id || selectedShiftType === String(type.id)}
                        onChange={(e) => {
                          const value = isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value);
                          setSelectedShiftType(value);
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className={`px-2 py-1 rounded text-xs ${getShiftTypeColor(type.id)}`}>
                        {type.label}
                      </span>
                      <span className="text-sm text-gray-600">
                        {type.start_time} - {type.end_time}
                        {type.start_time2 && type.end_time2 && (
                          <>, {type.start_time2} - {type.end_time2}</>
                        )}
                        {type.break_required && ' (休憩あり)'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* 使い方説明 */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                {deleteMode ? '削除方法' : '登録方法'}
              </h4>
              <ol className="text-xs text-gray-600 space-y-1">
                {deleteMode ? (
                  <>
                    <li>① スタッフを選択</li>
                    <li>② カレンダーの日付をクリック</li>
                    <li>③ 確認後、削除完了</li>
                  </>
                ) : (
                  <>
                    <li>① スタッフを選択</li>
                    <li>② 勤務形態を選択</li>
                    <li>③ カレンダーの日付をクリック</li>
                    <li>→ 即座に登録完了！</li>
                  </>
                )}
              </ol>
            </div>

            {/* 選択状態サマリー */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="text-sm">
                <div className="font-semibold text-blue-800 mb-1">
                  {deleteMode ? '削除準備完了' : '登録準備完了'}
                </div>
                <div className="text-blue-600">
                  選択スタッフ: {selectedStaffIds.length}名
                  {!deleteMode && (
                    <>
                      <br />
                      勤務形態: {getShiftTypeLabel(selectedShiftType)}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* シフト追加モーダル（個別登録モード） */}
      {showModal && !quickMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {selectedDate && format(selectedDate, 'M月d日(E)', { locale: ja })}のシフト
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* 既存のシフト */}
            {selectedDate && getShiftsForDate(selectedDate).length > 0 && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm font-semibold mb-2">登録済みシフト</div>
                {getShiftsForDate(selectedDate).map((shift) => (
                  <div key={shift.id} className="flex items-center justify-between py-1">
                    <span className="text-sm">
                      {shift.staff_name} ({shift.start_time.slice(0, 5)}-{shift.end_time.slice(0, 5)})
                    </span>
                    <button
                      onClick={() => handleDeleteShift(shift.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* シフト追加フォーム */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  スタッフ
                </label>
                <select
                  value={formData.staff_id}
                  onChange={(e) => setFormData({ ...formData, staff_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">選択してください</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.display_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    開始時間
                  </label>
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    終了時間
                  </label>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  シフトタイプ
                </label>
                <select
                  value={formData.shift_type}
                  onChange={(e) => setFormData({ ...formData, shift_type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {shiftTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.label} ({type.start_time} - {type.end_time})
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleAddShift}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  シフト追加
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 時給ルール手動適用モーダル */}
      {selectedShiftForRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-96 max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">時給ルールの手動適用</h3>
                <button
                  onClick={() => setSelectedShiftForRules(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm">
                  <div className="font-semibold">{selectedShiftForRules.staff_name}</div>
                  <div className="text-gray-600">
                    {format(new Date(selectedShiftForRules.shift_date), 'M月d日(E)', { locale: ja })}
                    {' '}
                    {selectedShiftForRules.start_time.slice(0, 5)} - {selectedShiftForRules.end_time.slice(0, 5)}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2">手動適用可能な時給ルール</h4>
                <div className="space-y-2">
                  {wageRules
                    .filter(rule => rule.type === 'peak' || rule.type === 'special')
                    .map(rule => {
                      const isApplied = appliedManualRules[`shift-${selectedShiftForRules.id}`]?.includes(rule.id);
                      const IconComponent = getIconComponent(rule.icon);
                      
                      return (
                        <button
                          key={rule.id}
                          onClick={() => {
                            const key = `shift-${selectedShiftForRules.id}`;
                            setAppliedManualRules(prev => {
                              const current = prev[key] || [];
                              if (isApplied) {
                                return {
                                  ...prev,
                                  [key]: current.filter(id => id !== rule.id)
                                };
                              } else {
                                return {
                                  ...prev,
                                  [key]: [...current, rule.id]
                                };
                              }
                            });
                          }}
                          className={`w-full p-3 rounded-lg border text-left transition-all ${
                            isApplied
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {IconComponent && (
                                <IconComponent className={`h-5 w-5 ${rule.icon_color || 'text-gray-600'}`} />
                              )}
                              <div>
                                <div className="font-medium">{rule.name}</div>
                                <div className="text-sm text-gray-600">+{rule.amount}円</div>
                              </div>
                            </div>
                            {isApplied && (
                              <Check className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedShiftForRules(null)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  適用
                </button>
                <button
                  onClick={() => {
                    setSelectedShiftForRules(null);
                    // 変更をキャンセル
                    const key = `shift-${selectedShiftForRules.id}`;
                    const originalRules = appliedManualRules[key] || [];
                    setAppliedManualRules(prev => ({
                      ...prev,
                      [key]: originalRules
                    }));
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 月初登録設定モーダル */}
      {showMonthlySettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-[600px] max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">月初登録設定</h3>
                <button
                  onClick={() => setShowMonthlySettings(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* 定休日設定 */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-2">定休日設定</h4>
                <div className="flex gap-2 flex-wrap">
                  {weekDays.map((day, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setMonthlySettings(prev => ({
                          ...prev,
                          closedDays: prev.closedDays.includes(index)
                            ? prev.closedDays.filter(d => d !== index)
                            : [...prev.closedDays, index]
                        }));
                      }}
                      className={`px-3 py-2 rounded-lg border transition-all ${
                        monthlySettings.closedDays.includes(index)
                          ? 'bg-red-100 border-red-300 text-red-700'
                          : 'bg-white border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {day}曜日
                    </button>
                  ))}
                </div>
              </div>

              {/* デフォルトシフト設定 */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold">デフォルトシフト</h4>
                  <button
                    onClick={() => {
                      setMonthlySettings(prev => ({
                        ...prev,
                        defaultShifts: [...prev.defaultShifts, {
                          staffName: '',
                          startTime: '10:00',
                          endTime: '19:00',
                          isOwner: false
                        }]
                      }));
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    追加
                  </button>
                </div>
                
                <div className="space-y-2">
                  {monthlySettings.defaultShifts.map((shift, index) => (
                    <div key={index} className="flex gap-2 items-center p-2 bg-gray-50 rounded">
                      <select
                        value={shift.staffName}
                        onChange={(e) => {
                          const newShifts = [...monthlySettings.defaultShifts];
                          newShifts[index].staffName = e.target.value;
                          setMonthlySettings(prev => ({ ...prev, defaultShifts: newShifts }));
                        }}
                        className="px-2 py-1 border rounded flex-1"
                      >
                        <option value="">スタッフを選択</option>
                        {staff.map(s => (
                          <option key={s.id} value={s.display_name}>
                            {s.display_name}
                          </option>
                        ))}
                      </select>
                      
                      <input
                        type="time"
                        value={shift.startTime}
                        onChange={(e) => {
                          const newShifts = [...monthlySettings.defaultShifts];
                          newShifts[index].startTime = e.target.value;
                          setMonthlySettings(prev => ({ ...prev, defaultShifts: newShifts }));
                        }}
                        className="px-2 py-1 border rounded"
                      />
                      
                      <span>〜</span>
                      
                      <input
                        type="time"
                        value={shift.endTime}
                        onChange={(e) => {
                          const newShifts = [...monthlySettings.defaultShifts];
                          newShifts[index].endTime = e.target.value;
                          setMonthlySettings(prev => ({ ...prev, defaultShifts: newShifts }));
                        }}
                        className="px-2 py-1 border rounded"
                      />
                      
                      <button
                        onClick={() => {
                          setMonthlySettings(prev => ({
                            ...prev,
                            defaultShifts: prev.defaultShifts.filter((_, i) => i !== index)
                          }));
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* 不定休設定 */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold mb-2">今月の不定休</h4>
                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth().map((date) => {
                    if (!isSameMonth(date, currentMonth)) return null;
                    
                    const dateStr = format(date, 'yyyy-MM-dd');
                    const isHoliday = monthlySettings.holidays.some(
                      h => format(h, 'yyyy-MM-dd') === dateStr
                    );
                    const dayOfWeek = getDay(date);
                    const isRegularClosed = monthlySettings.closedDays.includes(dayOfWeek);
                    
                    return (
                      <button
                        key={date.toISOString()}
                        onClick={() => {
                          if (isRegularClosed) return; // 定休日は選択不可
                          
                          setMonthlySettings(prev => ({
                            ...prev,
                            holidays: isHoliday
                              ? prev.holidays.filter(h => format(h, 'yyyy-MM-dd') !== dateStr)
                              : [...prev.holidays, date]
                          }));
                        }}
                        disabled={isRegularClosed}
                        className={`p-2 text-xs rounded transition-all ${
                          isRegularClosed
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : isHoliday
                              ? 'bg-orange-100 text-orange-700 border border-orange-300'
                              : 'bg-white border border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {format(date, 'd')}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  ※ オレンジ色が不定休です。定休日（グレー）は選択できません。
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={async () => {
                    try {
                      await apiClient.post('/shifts-enhanced/monthly-settings', {
                        closedDays: monthlySettings.closedDays,
                        holidays: monthlySettings.holidays.map(h => format(h, 'yyyy-MM-dd')),
                        defaultShifts: monthlySettings.defaultShifts
                      });
                      alert('設定を保存しました');
                      setShowMonthlySettings(false);
                    } catch (error) {
                      console.error('設定保存エラー:', error);
                      alert('設定の保存に失敗しました');
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  保存
                </button>
                <button
                  onClick={() => setShowMonthlySettings(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftCalendarEnhanced;
